/**
 * Angular imports.
 */
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { COUNTRIES_LIST, SG_BMS_NR_PND_1, TH_SERVIER_PROCESS ,TH_SERVIER_PRIVATE_PROCESS, TH_SERVIER_PUBLIC_PROCESS, TH_DAICHII_PROCESS} from 'projects/nga-PAP/src/app/constant/app.constant';
import { IdNamePair } from 'projects/nga-PAP/src/app/models/sign-up-chatbot.model';
import aeReportValidation from '../../../.../../../../../assets/static/ae-report-validation.json';

/**
 * Used to show the form for pediatric report.
 */
@Component({
  selector: 'pap-adverse-event-info-form',
  templateUrl: './adverse-event-info-form.component.html',
  styleUrls: ['./adverse-event-info-form.component.scss']
})
export class AdverseEventInfoFormComponent implements OnInit, OnChanges {
  @Input() functionality = '';
  @Input() formData: any;
  @Input() process: string;

  /**
   * Form pediatric report.
   */
  public adverseEventInfoForm: FormGroup;
  private disabled: boolean;

  /**
   * setter for the form controls.
   */
  get form(): { [key: string]: AbstractControl; } { return this.adverseEventInfoForm.controls; }
  get formValidity(): boolean { return this.adverseEventInfoForm.valid; }
  get formValues(): { adverseEventInfoForm: any; } { return { adverseEventInfoForm: this.adverseEventInfoForm.value }; }

  /**
   * Outcome list.
   */
  outcomeList = [
    { id: 1, name: 'REC: Completely recovered' },
    { id: 2, name: 'SEQ: Recovered with sequelae' },
    { id: 3, name: 'IMP: Condition improving' },
    { id: 4, name: 'UNC: Condition unchanged' },
    { id: 5, name: 'DET: Condition deteriorating' },
    { id: 6, name: 'FAT: Fatal /Death' },
    { id: 7, name: 'UNK: Outcome unknown' }
  ];
  servierOutcomeList = [
    { id: 1, name: 'Recovered' },
    { id: 2, name: 'Recovered with sequelae' },
    { id: 3, name: 'Recovering' },
    { id: 4, name: 'Not recovered' },
    { id: 5, name: 'Fatal' },
    { id: 6, name: 'Unknown' },
  ];
  bmsOutcomeList = [
    { id: 1, name: 'Resolved' },
    { id: 2, name: 'Resolving' },
    { id: 3, name: 'Did not Resolve' },
    { id: 4, name: 'Death' },
    { id: 5, name: 'Resolved with Sequelae' },
    { id: 6, name: 'Unknown' },
  ];
  daichiiOutcomeList = [
    { id: 1, name: 'Recovered without sequelae' },
    { id: 2, name: 'Recovered with sequelae' },
    { id: 3, name: 'Recovering' },
    { id: 4, name: 'Not recovered yet' },
    { id: 5, name: 'Death due to adverse reaction' },
    { id: 6, name: 'Death: health product may be contributory' },
    { id: 7, name: 'Death: unrelated to health product' },
    { id: 8, name: 'Unable to follow up' },
  ];

  selectedOutcome = '';
  /**
   * event casuality list
   */
  eventCausalityList = [
    { id: 1, name: 'S:Suspected/ Related' },
    { id: 2, name: 'NS: Not Suspected/ Not Related' },
    { id: 3, name: 'NA: Not Assessable' }
  ];
  /**
   * seriousness list
   */
  seriousnessCriteriaListNonServier = [
    { id: 1, name: 'D: Patient died' },
    { id: 2, name: 'LT: Life threatening' },
    { id: 3, name: 'HOSP: Required hospitalization or prolonged hospitalization' },
    { id: 4, name: 'DIS: Persistent or significant disability or incapacity' },
    { id: 5, name: 'MS: Medically significant' },
    { id: 6, name: 'CA: Congenital anomaly or birth defect' },
    { id: 7, name: 'NS: Non-Serious' }
  ];
  seriousnessCriteriaListServier = [
    { id: 1, name: 'Non-Serious' },
    { id: 2, name: 'Fatal' },
    { id: 3, name: 'Life-threatening' },
    { id: 4, name: 'Hospitalisation or prolongation of hospitalisation' },
    { id: 5, name: 'Persistent or significant disability or incapacity' },
    { id: 6, name: 'Congenital anomaly or birth defect' },
    { id: 7, name: 'Medically significant' }
  ];
  seriousnessCriteriaListBMS = [
    { id: 1, name: 'Life-threatening'},
    { id: 2, name: 'Hospitalisation or prolongation' },
    { id: 3, name: 'Congenital anomaly or birth defect' },
    { id: 4, name: 'Persistent or significant disability' },
    { id: 5, name:  'Resulted in Death'},
    { id: 6, name: 'Important Medical Event'}
  ];
  selectedSeriousCriteria = '';
  /**
   * casual relationship list
   */
  casualRelationShipList = [
    { id: 1, name: 'No' },
    { id: 2, name: 'Yes' },
    { id: 3, name: 'Not Applicable' }
  ];
  aeType = [
    { id: 1, name: 'Serious AE' },
    { id: 2, name: 'Non-Serious AE' }
  ];

  seriousnessCriteriaList: IdNamePair[] = [];

  /**
   * Necessary instances.
   */
  constructor(private fb: FormBuilder) { }

  /**
   * Calling initialize form.
   */
  ngOnInit(): void {
    this.disabled = this.functionality === 'view';
    this.initializeForm();
  }

  /**
   * to detect changes on incoming form values
   * @param changes changes on formData
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.formData) {
      this.initializeForm();
    }
    if (changes?.process) {
      this.updateFormWrtCondition();
    }
  }
  /**
   * function to restrict only blankspaces entry.
   */
    userInput(fieldName: string, event: any): void {
      if (event.target.value) {
        const value = event.target.value;
        if (value[0] === ' ') {
          this.adverseEventInfoForm.get(fieldName).setValue(value.trimStart());
        }
      }
    }

  /**
   * Initialize the form.
   */
  initializeForm(): void {
    this.adverseEventInfoForm = this.fb.group({
      aeOverview: new FormControl(
        {value : this.formData?.aeOverview ? this.formData?.aeOverview : '', disabled: this.disabled},
       this.checkForSingaporeCountry() ?
       [Validators.required, Validators.maxLength(10000)] : [Validators.required, Validators.maxLength(500)]
      ),
      descriptionAdverseEvent: new FormControl(
        {value : this.formData?.descriptionAdverseEvent ? this.formData?.descriptionAdverseEvent : '',
         disabled: this.disabled},
        [Validators.required, Validators.maxLength(10000)]),
      aeType: new FormControl(
        {value : this.formData?.aeType ? this.formData?.aeType : '', disabled: this.disabled}),
        deathCause: new FormControl(
          {value : this.formData?.deathCause ? this.formData?.deathCause : '', disabled: this.disabled}),
      outcome: new FormControl(
        {value : this.formData?.outcome ? this.formData?.outcome : '', disabled: this.disabled}),
      eventCausality: new FormControl(
        {value : this.formData?.eventCausality ? this.formData?.eventCausality : '', disabled: this.disabled}),
      seriousnessCriteria: new FormControl(
        {value : this.formData?.seriousnessCriteria ? this.formData?.seriousnessCriteria : '',
         disabled: this.disabled}),
      dateOfDeath: new FormControl(
          {value : this.formData?.dateOfDeath ? this.formData?.dateOfDeath : '', disabled: this.disabled}),
      symptomOnsetDate: new FormControl(
        {value : this.formData?.symptomOnsetDate ? this.formData?.symptomOnsetDate : '', disabled: this.disabled}),
      symptomEndDate: new FormControl(
        {value : this.formData?.symptomEndDate ? this.formData?.symptomEndDate : '', disabled: this.disabled}),
      genDisease: new FormControl(
        {value : this.formData?.genDisease ? this.formData?.genDisease : '', disabled: this.disabled}),
      drgRelation: new FormControl(
        {value : this.formData?.drgRelation ? this.formData?.drgRelation : '', disabled: this.disabled}),
    });
    this.updateFormWrtCondition();
  }

  /**
   * to update the form wrt condition
   */
  private updateFormWrtCondition(): void {
    if (this.checkForSingaporeCountry()){
      this.adverseEventInfoForm.get('aeOverview').setValidators([Validators.required, Validators.maxLength(10000)]);
      this.seriousnessCriteriaList = this.seriousnessCriteriaListBMS;
      this.outcomeList = this.bmsOutcomeList;
      this.adverseEventInfoForm.get('descriptionAdverseEvent').clearValidators();
      this.adverseEventInfoForm.get('descriptionAdverseEvent').setValidators(Validators.maxLength(10000));
      this.adverseEventInfoForm.get('descriptionAdverseEvent').updateValueAndValidity();
    }
    else {
      this.adverseEventInfoForm.get('aeOverview').setValidators([Validators.required, Validators.maxLength(500)]);
      this.seriousnessCriteriaList = this.seriousnessCriteriaListNonServier;
    }
    if (this.checkForServierProcess()) {
      this.adverseEventInfoForm.get('aeOverview').clearValidators();
      this.seriousnessCriteriaList = this.seriousnessCriteriaListServier;
      this.outcomeList = this.servierOutcomeList;
    }
    if (this.checkForDaichiiProcess()) {
      this.outcomeList = this.daichiiOutcomeList;
    }
    this.adverseEventInfoForm.get('aeOverview').updateValueAndValidity();
    this.adverseEventInfoForm.get('descriptionAdverseEvent').updateValueAndValidity();
    /**
     * set selected values
     */
    if (this.formData?.outcome) {
      this.selectedOutcome = this.outcomeList.find(outcome => outcome.name === this.formData?.outcome)?.name;
    }
    if (this.formData?.seriousnessCriteria) {
      this.selectedSeriousCriteria = this.seriousnessCriteriaList.find(serious =>
        serious.name === this.formData?.seriousnessCriteria)?.name;
    }
  }

  /**
   * Check if process is for servier chatbot flow
   */
  checkForServierProcess(): boolean {
    return (this.process === TH_SERVIER_PROCESS || this.process === TH_SERVIER_PRIVATE_PROCESS || this.process === TH_SERVIER_PUBLIC_PROCESS);
  }

  checkForSingaporeCountry(): boolean {
    return localStorage.getItem('country') === COUNTRIES_LIST.SINGAPORE.value || localStorage.getItem('country') === COUNTRIES_LIST.MALAYSIA.value;
  }

  checkIfFieldIsMandatory(fieldName: string): boolean {
    if (this.process === SG_BMS_NR_PND_1) {
      return aeReportValidation[this.process]?.adverseEventInfoForm[fieldName]?.mandatory;
    }
    return true;
  }
  getFieldName(fieldName: string): string {
    return aeReportValidation[this.process]?.adverseEventInfoForm[fieldName]?.title;
  }

  checkForSingaporeOnly(): boolean {
    return localStorage.getItem('country') === COUNTRIES_LIST.SINGAPORE.value;
  }

  checkForMalaysiaCountry(): boolean {
    return localStorage.getItem('country') === COUNTRIES_LIST.MALAYSIA.value;
  }
  checkForDaichiiProcess(): boolean {
    return this.process === TH_DAICHII_PROCESS;
  }
}
